import { utils } from '@grammarly/badger-ui'
import { useCallback, useState } from 'react'

export enum LocalStorageKey {
  grammarlyExperimentOverrides = 'grammarlyExperimentOverrides',
  // We save user text submitted in TextChecker component and use it to create a doc in Denali if user signs up or upgrades. This matches Funnel.
  checkedText = 'checkedText',
  // Keep count of GO Tools submissions
  countGoToolsSubmitAnonymous = 'countGoToolsSubmitAnonymous',
  countGoToolsSubmitSignedIn = 'countGoToolsSubmitSignedIn',
  expirationTimestampGoToolsSubmitAnonymous = 'expirationTimestampGoToolsSubmitAnonymous',
  expirationTimestampGoToolsSubmitSignedIn = 'expirationTimestampGoToolsSubmitSignedIn',

  // ACT-148: plan_page_plagiarism_personalization_v2
  isFromPlagiarismChecker = 'isFromPlagiarismChecker',

  // Keep count of AI Detector Submissions
  countAIDetectorSubmitAnonymous = 'countAIDetectorSubmitAnonymous',
  expirationTimestampAIDetectorSubmitAnonymous = 'expirationTimestampAIDetectorSubmitAnonymous',

  countPlagiarismSubmitAnonymous = 'countPlagiarismSubmitAnonymous',
  expirationPlagiarismSubmitAnonymous = 'expirationPlagiarismSubmitAnonymous',

  // ACT-205: reactivate_returning_users_without_persistent_client
  welcomeBackModalShowTimestamp = 'welcomeBackModalShowTimestamp',
  openEditorCtaClick = 'openEditorCtaClick',

  // Sprig tracking data
  sprigUserIds = 'userleap.ids',
}

export const isLocalStorageSupported = !!(IS_CLIENT_SIDE && typeof window !== 'undefined' && window.localStorage)

export function setLocalStorageItem(key: LocalStorageKey, value: string): void {
  if (isLocalStorageSupported) {
    try {
      window.localStorage.setItem(key, value)
    } catch (err) {
      // can throw if storage is full or in Safari private mode
    }
  }
}

export function getLocalStorageItem(key: LocalStorageKey): string | null {
  if (isLocalStorageSupported) {
    return window.localStorage.getItem(key)
  } else {
    return null
  }
}

export function removeLocalStorageItem(key: LocalStorageKey): void {
  if (isLocalStorageSupported) {
    window.localStorage.removeItem(key)
  }
}

export type SetLocalStorageItem = (key: LocalStorageKey, item: string) => void

export type RemoveLocalStorageItem = (key: LocalStorageKey) => void

// React version
type ReactLocalStorageItems = Partial<Record<LocalStorageKey, string>>
interface LocalStorageDataContextValue {
  items: ReactLocalStorageItems
}

interface LocalStorageActionsContextValue {
  setLocalStorageItem: SetLocalStorageItem
  removeLocalStorageItem: RemoveLocalStorageItem
}

const [LocalStorageDataProvider, useLocalStorageData] = utils.createContext<LocalStorageDataContextValue>({
  name: 'LocalStorageData',
})
const [LocalStorageActionsProvider, useLocalStorageActions] = utils.createContext<LocalStorageActionsContextValue>({
  name: 'LocalStorageActions',
})

interface LocalStorageProviderProps {
  children?: React.ReactNode
}

const getAllLocalStorageItems = () =>
  Object.keys(LocalStorageKey).reduce((obj, keyKey: string) => {
    const key = LocalStorageKey[keyKey as keyof typeof LocalStorageKey]
    const value = getLocalStorageItem(key)
    return { ...obj, [key]: value }
  }, {}) as ReactLocalStorageItems

/** Local storage synced with React state so that updating local storage triggers consumer rerenders. */
export const LocalStorageProvider = (props: LocalStorageProviderProps) => {
  const { children } = props
  const [items, setItems] = useState(getAllLocalStorageItems())

  const setLocalStorageItemReact = useCallback((key: LocalStorageKey, value: string) => {
    setLocalStorageItem(key, value)
    setItems((i) => {
      if (i[key] === value) {
        return i
      }
      return { ...i, [key]: value }
    })
  }, [])

  const removeLocalStorageItemReact = useCallback((key: LocalStorageKey) => {
    removeLocalStorageItem(key)
    setItems((i) => {
      if (typeof i[key] === 'undefined') {
        return i
      }
      const { [key]: _toRemove, ...rest } = i
      return { ...rest }
    })
  }, [])

  return (
    <LocalStorageActionsProvider
      value={{
        setLocalStorageItem: setLocalStorageItemReact,
        removeLocalStorageItem: removeLocalStorageItemReact,
      }}
    >
      <LocalStorageDataProvider value={{ items }}>{children}</LocalStorageDataProvider>
    </LocalStorageActionsProvider>
  )
}

export const useLocalStorageItem = (key: LocalStorageKey): string | undefined => {
  return useLocalStorageData().items[key]
}

export const useSetLocalStorageItem = (): SetLocalStorageItem => {
  return useLocalStorageActions().setLocalStorageItem
}

export const useRemoveLocalStorageItem = (): RemoveLocalStorageItem => {
  return useLocalStorageActions().removeLocalStorageItem
}
